import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lognai/synapse-design-system-v2';
import type {
  AnalysisSpec,
  VisualSeries,
  VisualSpec,
  DatasetField,
} from '@lognai/plugin-analytics';

const memberLabel = (member: string): string => {
  const name = member.split('.').pop() ?? member;
  return name.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDate = (value: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return null;
  const normalized =
    /T/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};

const formatNumber = (value: number, unit?: VisualSeries['unit'], currency = 'USD'): string => {
  if (unit === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (unit === 'percent') return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`;
  if (unit === 'days') return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })} days`;
  return value.toLocaleString('en-US', {
    minimumFractionDigits: unit === 'count' ? 0 : undefined,
    maximumFractionDigits: unit === 'count' ? 0 : 2,
  });
};

const formatCell = (
  value: string | number | null,
  unit?: VisualSeries['unit'],
  currency?: string
): string => {
  if (value == null || value === '') return 'Not available';
  if (typeof value === 'number') return formatNumber(value, unit, currency);
  const numeric = Number(value);
  if (unit && Number.isFinite(numeric)) return formatNumber(numeric, unit, currency);
  return formatDate(value) ?? value.replace(/_/g, ' ');
};

interface Props {
  labels?: Record<string, string>;
  rows?: Array<Record<string, string | number | null>>;
  fields?: DatasetField[];
  analysis: AnalysisSpec;
  visual: VisualSpec;
  fillHeight?: boolean;
}

export function VisualDataTable({
  analysis,
  visual,
  rows: executedRows,
  fields = [],
  labels = {},
  fillHeight = false,
}: Props) {
  const timeColumn = analysis.timeDimension
    ? `${analysis.timeDimension.member}.${analysis.timeDimension.granularity}`
    : undefined;
  const columns = (analysis.baselineComparison ? [
    'comparison.month', 'comparison.baseline', 'comparison.current', 'comparison.reduction', 'comparison.reduction_rate',
  ] : [
    ...analysis.dimensions,
    ...(timeColumn ? [timeColumn] : []),
    ...analysis.measures,
  ]).filter((member, index, all) => all.indexOf(member) === index);
  const rows = executedRows ?? [
    ...new Map(
      visual.series.flatMap((series) =>
        series.points.map((point) => [JSON.stringify(point.raw), point.raw] as const)
      )
    ).values(),
  ];
  const infoByMember = new Map(
    (analysis.baselineComparison ? visual.series.map(series => series.key) : analysis.measures).map((member) => [
      member,
      visual.series.find((series) => series.key === member || series.key.startsWith(`${member}:`)),
    ])
  );
  const unitByMember = new Map(
    [...infoByMember]
      .filter(([, series]) => series)
      .map(([member, series]) => [member, series!.unit])
  );

  if (!rows.length || !columns.length) return null;

  return (
    <div
      className={fillHeight ? 'flex h-full min-h-0 flex-col pt-4' : 'border-t border-border pt-4'}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">Data</h3>
        <span className="text-xs text-muted-foreground">
          {rows.length.toLocaleString()} {rows.length === 1 ? 'row' : 'rows'}
        </span>
      </div>
      <div
        className={
          fillHeight
            ? 'min-h-0 flex-1 overflow-auto border border-border'
            : 'max-h-[360px] overflow-auto border border-border'
        }
      >
        <Table density="compact">
          <TableHeader className="sticky top-0 z-10 bg-surface">
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column}
                  className={unitByMember.has(column) ? 'text-right' : undefined}
                >
                  {(analysis.baselineComparison ? ({ 'comparison.month': 'Month', 'comparison.baseline': analysis.baselineComparison.baselineLabel,
                    'comparison.current': analysis.baselineComparison.currentLabel, 'comparison.reduction': 'Reduction (negative means increase)',
                    'comparison.reduction_rate': 'Reduction percentage' } as Record<string, string>)[column] : undefined) ?? labels[column] ??
                    fields.find(
                      (f) =>
                        column.split('.')[1] === f.id ||
                        column.split('.')[1]?.startsWith(f.id + '_')
                    )?.name ??
                    memberLabel(column)}
                  {infoByMember.get(column)?.currency && (
                    <span className="ml-1 text-[10px] font-normal text-muted-foreground">
                      {infoByMember.get(column)?.currency}
                    </span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={`${visual.title}-${rowIndex}`}>
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    className={
                      unitByMember.has(column)
                        ? 'whitespace-nowrap text-right font-medium tabular-nums'
                        : 'min-w-[140px]'
                    }
                  >
                    {analysis.baselineComparison ? formatCell(
                      row[column] ?? null,
                      column === 'comparison.reduction_rate' ? 'percent' : column !== 'comparison.month' ? 'currency' : undefined,
                      analysis.baselineComparison.currency
                    ) : fields.length
                      ? String(row[column] ?? '')
                      : formatCell(
                          row[column] ?? null,
                          unitByMember.get(column),
                          infoByMember.get(column)?.currency
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
